import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, hasAdminPanelAccess } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useMessageNotifications } from '../../contexts/MessageNotificationContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import Button from '../Button';
import '@fortawesome/fontawesome-free/css/all.min.css';
import AppDropdown from 'components/AppDropdown';
import HamburgerMenu from './HamburgerMenu';
import MobileMenuSection from './MobileMenuSection';
import ProfilePicture from 'components/Profile/ProfilePicture';
import MessageCenterDropdown from './MessageCenterDropdown';
import ThemeToggle from '../theme/ThemeToggle';
import BrandLogo from '../BrandLogo';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

/** Default avatar image when user has no profile picture (user silhouette SVG). */
const DEFAULT_AVATAR_DATA_URI =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23adb5bd"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';

function UnreadDot() {
  return (
    <span
      className="sb-header-unread-dot position-absolute top-0 end-0 translate-middle rounded-circle bg-danger"
      style={{ width: '0.5rem', height: '0.5rem' }}
      aria-hidden="true"
    />
  );
}

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const { messageUnreadCount, conversationSummaries } = useMessageNotifications();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchDialogInputRef = useRef<HTMLInputElement>(null);

  const closeMenu = () => setMenuOpen(false);

  const openSearchDialog = () => {
    setSearchDialogOpen(true);
  };

  const closeSearchDialog = () => {
    setSearchDialogOpen(false);
  };

  const submitSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    closeSearchDialog();
    setSearchQuery('');
    navigate(`/?q=${encodeURIComponent(trimmed)}`);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitSearch(searchQuery);
  };

  useEffect(() => {
    if (searchDialogOpen) {
      searchDialogInputRef.current?.focus();
    }
  }, [searchDialogOpen]);

  const desktopNav = user ? (
    <div className="nav-right d-flex align-items-center">
      <ThemeToggle className="me-3 text-primary fs-5" />
      <Link
        to="/ilan-ver"
        className="btn btn-link text-primary border-0 p-2 me-1 flex-shrink-0"
        style={{ textDecoration: 'none' }}
        aria-label="İlan Ver"
        title="İlan Ver"
      >
        <i className="fas fa-plus-circle fs-5" aria-hidden="true" />
      </Link>
      <div className="d-flex align-items-center gap-1 me-2">
        <Link
          to="/notifications"
          className="btn btn-link text-primary position-relative border-0 p-2"
          style={{ textDecoration: 'none' }}
          aria-label={unreadCount > 0 ? 'Bildirimler (yeni)' : 'Bildirimler'}
        >
          <i className="fas fa-bell fs-5" />
          {unreadCount > 0 && <UnreadDot />}
        </Link>
        <MessageCenterDropdown compact />
      </div>
      <AppDropdown
        id="userDropdown"
        imgSrc={user?.profilePictureUrl ?? DEFAULT_AVATAR_DATA_URI}
        name={user?.name || 'Ahmet Yılmaz'}
      >
        <>
          <li className="px-3 py-3 border-bottom mb-2">
            <div className="d-flex align-items-center">
              <ProfilePicture width={50} height={50} />
              <div className="ms-3">
                <h6 className="mb-1 fw-bold">{user?.name || 'Ahmet Yılmaz'}</h6>
                <p className="small text-muted mb-0">{user?.email || 'ahmet.yilmaz@example.com'}</p>
              </div>
            </div>
          </li>
          <li><Link className="dropdown-item py-2" to="/profil"><i className="fas fa-user me-2 text-primary"></i>Profilim</Link></li>
          <li><Link className="dropdown-item py-2" to="/ilanlarim"><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link></li>
          <li><Link className="dropdown-item py-2" to="/gonderilerim"><i className="fas fa-video me-2 text-primary"></i>Gönderilerim</Link></li>
          <li><Link className="dropdown-item py-2" to="/favoriler"><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link></li>
          <li><Link className="dropdown-item py-2" to="/teknik-destek"><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link></li>
          {hasAdminPanelAccess(user) && (
            <li><Link className="dropdown-item py-2" to="/admin-panel"><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link></li>
          )}
          <li className="border-top mt-2">
            <button
              type="button"
              className="dropdown-item py-2 mt-2"
              onClick={logout}
            >
              <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
            </button>
          </li>
        </>
      </AppDropdown>
    </div>
  ) : (
    <div className="nav-right d-flex align-items-center flex-shrink-0">
      <ThemeToggle className="me-2 me-lg-3 text-primary fs-5" />
      <Link className="btn btn-outline-primary btn-sm me-1 me-lg-2" to="/ilan-ver" title="İlan Ver">
        <i className="fas fa-plus-circle me-lg-2" />
        <span className="d-none d-lg-inline">İlan Ver</span>
      </Link>
      <Link className="btn btn-primary btn-sm" to="/giris" title="Giriş Yap">
        <i className="fas fa-user me-lg-2" />
        <span className="d-none d-lg-inline">Giriş Yap</span>
      </Link>
    </div>
  );

  const mobileMenuContent = user ? (
    <>
      <MobileMenuSection title={<><i className="fas fa-user me-2 text-primary" />{user?.name || 'Hesabım'}</>}>
        <Link to="/profil" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-user me-2 text-primary"></i>Profilim</Link>
        <Link to="/ilanlarim" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link>
        <Link to="/gonderilerim" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-video me-2 text-primary"></i>Gönderilerim</Link>
        <Link to="/favoriler" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link>
        <Link to="/teknik-destek" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link>
        {hasAdminPanelAccess(user) && (
          <Link to="/admin-panel" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link>
        )}
        <button
          type="button"
          className="dropdown-item py-2 mt-2 w-100 text-start border-0 bg-transparent"
          onClick={() => {
            logout();
            closeMenu();
          }}
        >
          <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
        </button>
      </MobileMenuSection>
      <MobileMenuSection
        title={
          <>
            <i className="fas fa-envelope me-2 text-primary" />
            Mesajlar
            {messageUnreadCount > 0 && (
              <span className="badge bg-danger rounded-pill ms-2" aria-hidden="true">
                {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
              </span>
            )}
          </>
        }
      >
        <Link to="/mesajlar" className="dropdown-item py-2" onClick={closeMenu}>
          <i className="fas fa-envelope me-2 text-primary" />
          Tüm Mesajlar
        </Link>
        {conversationSummaries.slice(0, 3).map((s) => (
          <Link
            key={s.conversationId}
            to={`/mesajlar?conversationId=${s.conversationId}`}
            className="dropdown-item py-2 d-flex flex-column"
            onClick={closeMenu}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold small text-truncate" style={{ maxWidth: '70%' }}>{s.peerDisplayName}</span>
              <small className="text-muted">{formatRelativeTime(s.lastMessageAt)}</small>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <small className="text-muted text-truncate" style={{ maxWidth: '85%' }}>{s.lastMessagePreview ?? ''}</small>
              {s.unreadMessageCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-1">{s.unreadMessageCount}</span>
              )}
            </div>
          </Link>
        ))}
        {conversationSummaries.length === 0 && (
          <div className="dropdown-item py-2 text-muted small">Henüz mesaj yok.</div>
        )}
      </MobileMenuSection>
      <Link to="/notifications" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom position-relative" onClick={closeMenu}>
        <i className="fas fa-bell me-2 text-primary" />
        Bildirimler
        {unreadCount > 0 && (
          <span className="sb-header-unread-dot rounded-circle bg-danger ms-2" style={{ width: '0.5rem', height: '0.5rem', display: 'inline-block' }} aria-hidden="true" />
        )}
      </Link>
      <Link to="/ilan-ver" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom" onClick={closeMenu}>
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <div className="d-flex align-items-center py-3 border-bottom">
        <ThemeToggle className="text-primary fs-5" showLabel />
      </div>
    </>
  ) : (
    <>
      <div className="d-flex align-items-center py-3 border-bottom">
        <ThemeToggle className="text-primary fs-5" showLabel />
      </div>
      <Link to="/ilan-ver" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom" onClick={closeMenu}>
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <Link to="/giris" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom">
        <i className="fas fa-user me-2 text-primary" />
        Giriş Yap
      </Link>
    </>
  );

  return (
    <header className="header sticky-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
      <nav className="navbar navbar-expand-lg py-2 w-100">
        <div className="container sb-navbar-container">
          <div className="row align-items-center g-2 flex-grow-1 w-100 sb-navbar-row">
            <div className="col-auto col-lg-3 d-flex align-items-center">
              <Link className="navbar-brand fw-bold text-primary mb-0 py-0" to="/">
                <BrandLogo />
              </Link>
            </div>

            <div className="col d-flex align-items-center sb-navbar-main col-lg-9">
              {isMobile ? (
                <Button
                  type="button"
                  className="btn btn-link header-toolbar-icon p-2 flex-shrink-0"
                  onClick={openSearchDialog}
                  aria-label="Arama"
                >
                  <i className="fas fa-search fa-lg" aria-hidden />
                </Button>
              ) : (
                <div className="search-box flex-grow-1 min-w-0">
                  <form className="d-flex" onSubmit={handleSearchSubmit}>
                    <div className="input-group">
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Dizüstü, masaüstü, parça ara..."
                        aria-label="Arama"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Button className="btn btn-primary" type="submit">
                        <i className="fas fa-search"></i>
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {!isMobile && desktopNav}

              {isMobile && (
                <Button
                  type="button"
                  className="btn btn-link header-toolbar-icon p-2 ms-2 flex-shrink-0"
                  onClick={() => setMenuOpen(true)}
                  aria-label="Menüyü aç"
                >
                  <i className="fas fa-bars fa-lg" aria-hidden />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {isMobile && (
        <HamburgerMenu isOpen={menuOpen} onClose={closeMenu}>
          {mobileMenuContent}
        </HamburgerMenu>
      )}

      {searchDialogOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeSearchDialog}
            aria-hidden="true"
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="headerSearchDialogTitle"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="headerSearchDialogTitle">
                    Arama
                  </h5>
                  <Button
                    type="button"
                    className="btn-close"
                    onClick={closeSearchDialog}
                    aria-label="Kapat"
                  />
                </div>
                <form onSubmit={handleSearchSubmit}>
                  <div className="modal-body">
                    <input
                      ref={searchDialogInputRef}
                      type="search"
                      className="form-control"
                      placeholder="Dizüstü, masaüstü, parça ara..."
                      aria-label="Arama"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="modal-footer">
                    <Button type="button" className="btn btn-secondary" onClick={closeSearchDialog}>
                      İptal
                    </Button>
                    <Button type="submit" className="btn btn-primary">
                      <i className="fas fa-search me-2" aria-hidden />
                      Ara
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <style>
        {`
        /* Header Styles - Solid Background + separation from page */
        .header {
            background-color: var(--sb-bg, #ffffff) !important;
            opacity: 1;
            transition: background-color 200ms ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            z-index: 1040;
        }

        [data-bs-theme="dark"] .header {
            background-color: var(--sb-bg, #212529) !important;
            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
        }

        .header .navbar .container,
        .header .navbar .sb-navbar-container {
            align-items: center;
        }

        .header .navbar .sb-navbar-main {
            gap: 0.5rem;
        }

        .header .navbar .nav-right {
            align-items: center;
            flex-shrink: 0;
            min-width: 0;
            margin-left: auto;
        }

        @media (min-width: 992px) {
          .header .navbar .sb-navbar-container > .row {
            --bs-gutter-x: 1.5rem;
          }
        }

        @media (max-width: 991px) {
          .header .navbar .sb-navbar-row {
            flex-wrap: nowrap;
          }
        }

        /* Toolbar icons: always contrast with header surface (incl. mobile hamburger) */
        .header .navbar .header-toolbar-icon,
        .header .navbar .header-toolbar-icon i {
            color: var(--bs-primary) !important;
        }

        .header .navbar .btn-link.header-toolbar-icon:hover,
        .header .navbar .btn-link.header-toolbar-icon:focus {
            color: var(--bs-primary) !important;
            opacity: 0.85;
        }

        .header .navbar .navbar-brand i {
            color: var(--bs-primary) !important;
        }

        .header .navbar .nav-right .btn-link i,
        .header .navbar .nav-right .btn-link.text-primary {
            color: var(--bs-primary) !important;
        }

        .header .navbar .nav-right .btn-outline-primary i {
            color: inherit;
        }

        .header .navbar .btn-primary i {
            color: #fff !important;
        }

        /* Dropdown Styles */
        .dropdown-menu {
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
            animation: fadeIn 0.3s ease;
        }

        .dropdown-header {
            font-weight: 600;
            letter-spacing: 0.5px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .dropdown-item {
            padding: 0.6rem 1.2rem;
            transition: all 0.2s;
        }

        .dropdown-item:hover {
            background-color: #f0f7ff;
            padding-left: 1.5rem;
        }

        .dropdown-item:active {
            background-color: #0d6efd;
            color: white;
        }

        .dropdown-item:active i {
            color: white !important;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .btn-link {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .btn-link:focus, .btn-link:hover, .btn-link:active {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .dropdown-toggle::after {
            vertical-align: middle;
            margin-left: 0.4em;
        }

        /* Mobile: keep logo + search icon + hamburger in one row */
        @media (max-width: 991px) {
          .header .navbar .col-lg-3 {
            max-width: 55%;
          }
          .navbar-brand {
            flex-shrink: 0;
            font-size: 0.95rem;
          }
          .header .navbar .header-toolbar-icon {
            flex-shrink: 0;
          }
          .header .navbar .sb-navbar-main {
            justify-content: flex-end;
            gap: 0.25rem;
          }
        }
        `}
      </style>
    </header>
  );
};

export default Navbar;
